import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, TestTube } from "lucide-react";
import { LAB } from "@/constants/testIds";
import { LOINC_SYSTEM, listLabs } from "@/lib/labApi";
import "@/styles/emr-catalog.css";

function apiErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join("; ");
  return "Unable to load laboratory catalog.";
}

export default function LabsPage() {
  const [items, setItems] = useState([]);
  const [system, setSystem] = useState(LOINC_SYSTEM);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listLabs({ active: true });
        if (cancelled) return;
        setItems(data.items || []);
        setSystem(data.system || LOINC_SYSTEM);
      } catch (err) {
        if (!cancelled) setError(apiErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="emr-catalog" data-testid={LAB.page}>
      <div className="emr-catalog__pagehead">
        <h1 className="emr-catalog__title" data-testid={LAB.title}>
          Laboratory Catalog
        </h1>
      </div>

      <div className="emr-catalog__workspace">
        <div className="emr-catalog__side">
          <section className="card emr-catalog__section emr-catalog__purpose">
            <h2 className="emr-catalog__section-title">Purpose</h2>
            <p className="emr-catalog__copy">
              Development/demo laboratory master for future Encounter orders. Not a patient
              lab list and not an ordering screen.
            </p>
          </section>

          <section
            data-testid={LAB.banner}
            className="card emr-catalog__section emr-catalog__notice emr-catalog__section--notice"
          >
            <h2 className="emr-catalog__section-title">Reference data</h2>
            <p className="emr-catalog__copy">
              Small demo subset only. Each row is catalog/reference data ({system}). Not a
              FHIR ServiceRequest.
            </p>
          </section>
        </div>

        <section className="card emr-catalog__section emr-catalog__catalog">
          <h2 className="emr-catalog__section-title">Laboratory tests</h2>
          {error ? (
            <div data-testid={LAB.errorState} className="emr-catalog__error">
              <AlertCircle className="emr-catalog__error-icon" aria-hidden="true" />
              <p className="emr-catalog__error-copy">{error}</p>
              <Button
                variant="outline"
                className="btn emr-catalog__retry"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </div>
          ) : loading ? (
            <div data-testid={LAB.loading} className="emr-catalog__status-msg">
              Loading laboratory catalog…
            </div>
          ) : !items.length ? (
            <div data-testid={LAB.emptyState} className="emr-catalog__empty">
              <TestTube className="emr-catalog__empty-icon" aria-hidden="true" />
              <p>No demo laboratory tests are seeded yet.</p>
            </div>
          ) : (
            <div className="emr-catalog__table-wrap">
              <table data-testid={LAB.table} className="striped highlight emr-catalog__table">
                <thead>
                  <tr>
                    <th>Lab Test</th>
                    <th>Short Name</th>
                    <th>Category</th>
                    <th>Specimen</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id || row.catalogKey} data-testid={`${LAB.row}-${row.id}`}>
                      <td className="emr-catalog__name">{row.testName}</td>
                      <td>{row.shortName}</td>
                      <td className="emr-catalog__muted">{row.category || "—"}</td>
                      <td className="emr-catalog__muted">{row.specimen || "—"}</td>
                      <td>
                        {row.active !== false ? (
                          <span className="emr-catalog__status emr-catalog__status--active">Active</span>
                        ) : (
                          <span className="emr-catalog__status emr-catalog__status--inactive">Inactive</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
